/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useState } from "react";
import API, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  CurrencyGbp, Plus, FileText, ArrowsClockwise, Warning, X, Trash,
} from "@phosphor-icons/react";

const fmt = (v) => `£${(Number(v) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Per-case Accounts tab — renders inside CaseDetailPage. Two sub-ledgers
 * (Client + Office) plus a "Post entry" dialog and ledger PDF downloads.
 *
 * Designed to be embedded; receives `caseId` from the parent route. Reuses
 * `API` axios instance for auth + base URL.
 */
export default function CaseAccountsTab({ caseId }) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [side, setSide] = useState("client");
  const [ledger, setLedger] = useState(null);
  const [pendingDrafts, setPendingDrafts] = useState([]);
  const [pendingJournalReqs, setPendingJournalReqs] = useState([]);
  const [coa, setCoa] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postOpen, setPostOpen] = useState(false);

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const [ld, accounts, drafts, jreqs] = await Promise.all([
        API.get(`/accounts/ledger/${caseId}/${side}`),
        coa.length ? { data: coa } : API.get("/accounts/coa"),
        API.get(`/accounts/draft-invoices?status=pending&case_id=${caseId}`),
        API.get(`/accounts/journal-requests?status=pending&case_id=${caseId}`),
      ]);
      setLedger(ld.data);
      setPendingDrafts(drafts.data || []);
      setPendingJournalReqs(jreqs.data || []);
      if (!coa.length) setCoa(accounts.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load ledger");
    } finally {
      setLoading(false);
    }
  }, [caseId, side, coa.length]);

  useEffect(() => { load(); }, [load]);

  const downloadLedgerPdf = useCallback(async () => {
    try {
      const resp = await API.get(`/accounts/ledger/${caseId}/${side}.pdf?t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.download = `${side}-ledger-${caseId.slice(0, 8)}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed");
    }
  }, [caseId, side]);

  const onDeleteRequest = useCallback(async (id) => {
    if (!window.confirm("Delete this pending entry request? This cannot be undone.")) return;
    try {
      await API.delete(`/accounts/journal-requests/${id}`);
      toast.success("Entry request deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  }, [load]);

  // Reverse a posted ledger entry. SRA-compliant: we never hard-delete a
  // journal — instead a contra entry is posted that nets the original to
  // zero. The original row is rendered struck-through in the table and
  // the contra appears as a fresh row beneath it. Admin-only on the
  // backend (`/accounts/journal/{id}/reverse`).
  const onReverseEntry = useCallback(async (entryId) => {
    const reason = window.prompt(
      "Delete this transaction?\n\nThis posts a reversing entry — required for SRA audit trail.\n\nReason for reversal:",
    );
    if (reason == null) return;
    if (!reason.trim()) {
      toast.error("A reason is required");
      return;
    }
    try {
      await API.post(`/accounts/journal/${entryId}/reverse`, { reason: reason.trim() });
      toast.success("Transaction reversed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reverse failed");
    }
  }, [load]);

  if (loading && !ledger) {
    return <p className="text-xs text-slate-400 py-8 text-center">Loading ledger…</p>;
  }

  return (
    <div className="space-y-4" data-testid="case-accounts-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-xs">
          {[["client", "Client account"], ["office", "Office account"]].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setSide(k)}
              data-testid={`accounts-side-${k}`}
              className={`px-3 py-1.5 ${side === k ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setPostOpen(true)} size="sm" className="h-8 text-xs gap-1.5 text-white" style={{ background: "#100f5d" }} data-testid="accounts-request-btn">
            <Plus weight="bold" className="w-3.5 h-3.5" /> Request entry
          </Button>
          <Button onClick={downloadLedgerPdf} variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid="accounts-pdf-btn">
            <FileText weight="bold" className="w-3.5 h-3.5" /> Ledger PDF
          </Button>
          <Button onClick={load} variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid="accounts-reload-btn">
            <ArrowsClockwise weight="bold" className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Balances strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="accounts-balances">
        {Object.entries(ledger?.balances || {}).map(([code, bal]) => (
          <div key={code} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`accounts-balance-${code}`}>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{code}</p>
            <p className="text-xs text-slate-600">{coa.find((a) => a.code === code)?.name || "—"}</p>
            <p className={`text-base font-bold mt-1 ${bal < 0 ? "text-rose-600" : "text-slate-900"}`} style={{ fontFamily: "Outfit, sans-serif" }}>{fmt(bal)}</p>
          </div>
        ))}
      </div>

      {/* Pending journal-entry requests — solicitor-raised, awaiting
          accounts-team approval. Each shows the form fields the solicitor
          captured (payee, payment method, bank details, reference) — the
          accounts team chooses the actual nominals at approval. Creator
          can delete a still-pending request. */}
      {pendingJournalReqs.filter((r) => !r.side || r.side === side).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid="case-pending-journal-reqs">
          <div className="flex items-center gap-1.5">
            <Warning weight="bold" className="w-3.5 h-3.5 text-amber-700" />
            <p className="text-xs font-semibold text-amber-900">
              Pending entry request{pendingJournalReqs.length === 1 ? "" : "s"} — awaiting accounts approval
            </p>
          </div>
          {pendingJournalReqs.filter((r) => !r.side || r.side === side).map((r) => (
            <div key={r.id} className="text-xs bg-white border border-amber-100 rounded px-2.5 py-2" data-testid={`pending-journal-req-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-800">{r.narrative}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    {r.posting_date} · {r.created_by_name || "—"} ·
                    <span className="capitalize ml-1">{r.simple_form?.transaction_type || "—"}</span>
                  </p>
                  {r.simple_form && (
                    <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] text-slate-600">
                      {r.simple_form.payee_address && <div><span className="text-slate-400">Address:</span> {r.simple_form.payee_address}</div>}
                      {r.simple_form.account_number && <div><span className="text-slate-400">Account:</span> {r.simple_form.account_number}</div>}
                      {r.simple_form.sort_code && <div><span className="text-slate-400">Sort:</span> {r.simple_form.sort_code}</div>}
                      {r.simple_form.reference && <div><span className="text-slate-400">Ref:</span> {r.simple_form.reference}</div>}
                      {r.simple_form.notes && <div className="sm:col-span-2"><span className="text-slate-400">Notes:</span> {r.simple_form.notes}</div>}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <p className="text-sm font-bold tabular-nums text-slate-900">{fmt(r.total_debit)}</p>
                  <button
                    type="button"
                    onClick={() => onDeleteRequest(r.id)}
                    className="text-[10px] text-rose-600 hover:underline"
                    data-testid={`delete-req-${r.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
          <p className="text-[10px] text-amber-700 italic">
            Admin can approve (choosing the correct nominals) or reject on the Accounts page → Drafts tab.
          </p>
        </div>
      )}

      {/* Pending invoice drafts — visible only on the Office side because
          they'll post to office accounts (Dr 1200 / Cr 4000+2200+4010)
          once an admin approves them on the firm Accounts page. */}
      {side === "office" && pendingDrafts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid="case-pending-drafts">
          <div className="flex items-center gap-1.5">
            <Warning weight="bold" className="w-3.5 h-3.5 text-amber-700" />
            <p className="text-xs font-semibold text-amber-900">
              {pendingDrafts.length === 1 ? "1 pending invoice" : `${pendingDrafts.length} pending invoices`} — awaiting accounts approval
            </p>
          </div>
          {pendingDrafts.map((d) => (
            <div key={d.id} className="flex items-center justify-between text-xs bg-white border border-amber-100 rounded px-2.5 py-2" data-testid={`pending-draft-${d.id}`}>
              <div>
                <p className="font-medium text-slate-800">{fmt(d.totals.total)} <span className="text-[10px] font-normal text-slate-500 ml-1">(legal {fmt(d.totals.legal_net)} + VAT {fmt(d.totals.legal_vat)} + disbs {fmt(d.totals.disbursements + d.totals.third_party)})</span></p>
                <p className="text-[10px] text-slate-500">Created {new Date(d.created_at).toLocaleString("en-GB")} by {d.created_by_name || "—"}</p>
              </div>
              <span className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide">Pending</span>
            </div>
          ))}
          <p className="text-[10px] text-amber-700 italic">
            These will appear in the ledger below once an admin approves them on the Accounts page.
          </p>
        </div>
      )}

      {/* Entries table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Narrative</th>
              <th className="px-3 py-2 text-left">Acct</th>
              <th className="px-3 py-2 text-right">Debit</th>
              <th className="px-3 py-2 text-right">Credit</th>
              <th className="px-3 py-2 text-right">Running</th>
              {isAdmin && <th className="px-2 py-2 w-8" />}
            </tr>
          </thead>
          <tbody>
            {(() => {
              // SRA-style client ledger view: solicitors only need to see
              // ONE row per journal entry — the Client Bank (1010)
              // movement. The mirrored 2010 Client Liability line is
              // accountancy plumbing and just duplicates the visible
              // entry. Office ledger still shows every line because
              // those journals legitimately touch multiple nominals
              // (e.g. 1020 + 4000 + 4010 on an invoice posting).
              const allRows = ledger?.rows || [];
              const visibleRows = side === "client"
                ? allRows.filter((r) => r.account_code === "1010")
                : allRows;
              if (visibleRows.length === 0) {
                return (
                  <tr><td colSpan={isAdmin ? "7" : "6"} className="px-3 py-6 text-center text-slate-400 italic">No entries yet — post the first transaction with the button above.</td></tr>
                );
              }
              return visibleRows.map((r, idx) => (
                <tr key={`${r.entry_id}-${idx}`} className={`border-t border-slate-100 ${r.reversed ? "text-slate-400 line-through" : "text-slate-700"}`} data-testid={`accounts-row-${idx}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{r.posting_date}</td>
                  <td className="px-3 py-2">{r.narrative}{r.memo ? ` — ${r.memo}` : ""}</td>
                  <td className="px-3 py-2 font-mono">{r.account_code}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.debit ? fmt(r.debit) : ""}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.credit ? fmt(r.credit) : ""}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{fmt(r.running_balance)}</td>
                  {isAdmin && (
                    <td className="px-1 py-2 text-right">
                      {!r.reversed && r.source !== "reversal" && (
                        <button
                          type="button"
                          onClick={() => onReverseEntry(r.entry_id)}
                          className="w-6 h-6 inline-flex items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Reverse this transaction (posts a contra entry)"
                          data-testid={`accounts-row-reverse-${idx}`}
                        >
                          <Trash weight="bold" className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ));
            })()}
          </tbody>
        </table>
      </div>

      <SimpleRequestDialog
        open={postOpen}
        onClose={() => setPostOpen(false)}
        caseId={caseId}
        side={side}
        onPosted={() => { setPostOpen(false); load(); }}
      />
    </div>
  );
}

const TXN_TYPES = [
  { value: "receipt", label: "Receipt (money in)" },
  { value: "payment", label: "Payment (money out)" },
  { value: "transfer", label: "Transfer (between ledgers)" },
];

const METHODS = ["BACS", "CHAPS", "Faster Payment", "Cheque", "Card", "Cash", "Standing Order", "Other"];

/**
 * Solicitor-facing single-entry request form. Captures business facts only
 * — payee, payment method, bank details, amount, reference. The accounts
 * team chooses the actual nominal codes on approval, keeping solicitors
 * out of bookkeeping decisions.
 */
function SimpleRequestDialog({ open, onClose, caseId, side, onPosted }) {
  const [form, setForm] = useState({
    transaction_type: "payment",
    method: "CHAPS",
    payee: "",
    payee_address: "",
    account_number: "",
    sort_code: "",
    reference: "",
    amount: "",
    notes: "",
  });
  const [postingDate, setPostingDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        transaction_type: "payment",
        method: "CHAPS",
        payee: "",
        payee_address: "",
        account_number: "",
        sort_code: "",
        reference: "",
        amount: "",
        notes: "",
      });
      setPostingDate(new Date().toISOString().slice(0, 10));
    }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.payee.trim()) { toast.error("Payee is required"); return; }
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be greater than zero"); return; }
    setSaving(true);
    try {
      await API.post("/accounts/journal-requests", {
        case_id: caseId,
        side,
        transaction_type: form.transaction_type,
        method: form.method,
        payee: form.payee.trim(),
        payee_address: form.payee_address.trim() || null,
        account_number: form.account_number.trim() || null,
        sort_code: form.sort_code.trim() || null,
        reference: form.reference.trim() || null,
        notes: form.notes.trim() || null,
        amount: amt,
        posting_date: postingDate,
      });
      toast.success("Entry request submitted for accounts approval");
      onPosted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submit failed");
    } finally {
      setSaving(false);
    }
  };

  const isOutgoing = form.transaction_type === "payment" || form.transaction_type === "transfer";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl" data-testid="post-entry-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
            Request entry ({side === "client" ? "Client" : "Office"} ledger)
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Transaction type *</Label>
              <Select value={form.transaction_type} onValueChange={(v) => set("transaction_type", v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="req-txn-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TXN_TYPES.map((t) => (<SelectItem key={t.value} value={t.value} className="text-xs">{t.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Method *</Label>
              <Select value={form.method} onValueChange={(v) => set("method", v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="req-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {METHODS.map((m) => (<SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Payee *</Label>
            <Input value={form.payee} onChange={(e) => set("payee", e.target.value)} placeholder="e.g. HM Land Registry / Smith Conveyancing / John Smith" className="h-8 text-xs" data-testid="req-payee" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Payee address</Label>
            <Input value={form.payee_address} onChange={(e) => set("payee_address", e.target.value)} placeholder="Optional — postal address" className="h-8 text-xs" data-testid="req-payee-address" />
          </div>
          {isOutgoing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Account number {isOutgoing && <span className="text-slate-400">(for outgoing)</span>}</Label>
                <Input value={form.account_number} onChange={(e) => set("account_number", e.target.value)} placeholder="12345678" className="h-8 text-xs" data-testid="req-account-number" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Sort code</Label>
                <Input value={form.sort_code} onChange={(e) => set("sort_code", e.target.value)} placeholder="00-00-00" className="h-8 text-xs" data-testid="req-sort-code" />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Reference number</Label>
              <Input value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="Your ref / payee ref" className="h-8 text-xs" data-testid="req-reference" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Amount * (£)</Label>
              <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0.00" className="h-8 text-xs text-right" data-testid="req-amount" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Date</Label>
            <Input type="date" value={postingDate} onChange={(e) => setPostingDate(e.target.value)} className="h-8 text-xs" data-testid="req-date" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Notes for accounts team</Label>
            <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Optional — any extra context" className="h-8 text-xs" data-testid="req-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="post-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="text-white" style={{ background: "#100f5d" }} data-testid="post-submit-btn">
            {saving ? "Submitting…" : "Submit for approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
